import { useEffect, useState } from "react";
import api, { currency, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BulkImportDialog from "@/components/BulkImportDialog";
import { Plus, Search, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", pan: "", occupation: "", monthly_income: "" });
    const canCreate = ["ceo", "office_admin", "marketing_executive", "agent", "telecaller", "branch_manager"].includes(user.role);
    const canBulk = ["ceo", "office_admin", "marketing_executive", "telecaller", "branch_manager"].includes(user.role);

    const load = async () => {
        try { const { data } = await api.get("/customers"); setRows(data); } catch (e) { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, monthly_income: form.monthly_income ? Number(form.monthly_income) : null };
            await api.post("/customers", payload);
            toast.success("Customer added");
            setOpen(false);
            setForm({ name: "", phone: "", email: "", address: "", pan: "", occupation: "", monthly_income: "" });
            load();
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail));
        }
    };

    const filtered = rows.filter((r) =>
        (r.name + r.phone + (r.email || "")).toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Directory</p>
                    <h1 className="font-serif text-3xl text-komma-navy">Customers & Leads</h1>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        {canBulk && <BulkImportDialog onImported={load} />}
                        <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-komma-navy hover:bg-komma-navy-light" data-testid="add-customer-btn">
                                <Plus size={16} className="mr-2" /> New Customer
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle className="font-serif text-komma-navy">Add Customer</DialogTitle></DialogHeader>
                            <form onSubmit={create} className="space-y-3">
                                {[
                                    ["name", "Full name", true],
                                    ["phone", "Phone", true],
                                    ["email", "Email"],
                                    ["pan", "PAN"],
                                    ["occupation", "Occupation"],
                                    ["monthly_income", "Monthly income (₹)"],
                                    ["address", "Address"],
                                ].map(([k, label, req]) => (
                                    <div key={k}>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">{label}</Label>
                                        {k === "address" ? (
                                            <Textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`cust-${k}`} />
                                        ) : (
                                            <Input required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-10" data-testid={`cust-${k}`} />
                                        )}
                                    </div>
                                ))}
                                <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="cust-submit">Save</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            <div className="komma-card p-4">
                <div className="flex items-center gap-3">
                    <Search size={16} className="text-gray-400" />
                    <Input placeholder="Search by name, phone or email…" value={q} onChange={(e) => setQ(e.target.value)}
                        className="border-0 focus-visible:ring-0" data-testid="customer-search" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((c) => (
                    <div key={c.id} className="komma-card p-5" data-testid={`customer-${c.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="font-serif text-lg text-komma-navy">{c.name}</p>
                                <p className="text-xs text-gray-500">{c.occupation || "—"}</p>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest bg-komma-gold/10 text-komma-navy px-2 py-1 rounded">PAN {c.pan || "—"}</span>
                        </div>
                        <div className="komma-gold-hairline my-4" />
                        <div className="space-y-1.5 text-sm">
                            <p className="flex items-center gap-2 text-gray-700"><Phone size={13} className="text-komma-gold" /> {c.phone}</p>
                            {c.email && <p className="flex items-center gap-2 text-gray-700"><Mail size={13} className="text-komma-gold" /> {c.email}</p>}
                        </div>
                        <div className="mt-4 flex justify-between text-xs text-gray-500">
                            <span>Monthly income</span>
                            <span className="font-semibold text-komma-navy">{currency(c.monthly_income)}</span>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full komma-card p-12 text-center">
                        <p className="font-serif text-xl text-komma-navy">No customers yet</p>
                        <p className="text-sm text-gray-500 mt-1">Add your first customer to start a loan file.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
